import { useSearchParams } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Button } from '@shared/ui/primitives/Button';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { platform } from '@shared/lib/platform';

/**
 * Embedded webview wrapper. Web mode renders an `<iframe>` directly;
 * native opens the in-app browser via @capacitor/browser to honor the
 * estate's external content security policies (which often disallow
 * webview iframes due to X-Frame-Options).
 *
 * The route accepts `?url=...&title=...&allow=...` mirroring the Angular
 * IframeComponent contract.
 */
export function IframePage(): JSX.Element {
  const [params] = useSearchParams();
  const url = params.get('url');
  const title = params.get('title') ?? 'Embedded';
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (!url) return;
    if (!platform.isNative()) return;

    let cancelled = false;
    setOpening(true);
    Browser.open({ url, presentationStyle: 'popover' })
      .catch((e) => console.warn('[iframe] Browser open failed:', e))
      .finally(() => { if (!cancelled) setOpening(false); });

    return () => {
      cancelled = true;
      Browser.close().catch(() => { /* noop */ });
    };
  }, [url]);

  if (!url) {
    return (
      <Page>
        <EmptyState
          icon={<ExternalLink className="h-7 w-7" strokeWidth={1.5} />}
          title="Nothing to embed."
          description="This screen needs a URL — open it from a tile or link."
        />
      </Page>
    );
  }

  if (platform.isNative()) {
    return (
      <Page>
        <div className="flex flex-col gap-4 mt-4">
          <h1 className="font-display text-display-xl text-ink leading-tight">{title}</h1>
          <p className="text-body text-ink-secondary">Opens in the in-app browser.</p>
          {opening ? <Skeleton className="h-44" /> : null}
          <Button onClick={() => Browser.open({ url, presentationStyle: 'popover' })}>
            <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
            Open
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <div className="absolute inset-0">
      <iframe
        title={title}
        src={url}
        className="h-full w-full border-0"
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}
